/**
 * Media Quotes (sections/media-quotes.liquid)
 *
 * 1. Mobile slider dots: sync active dot with scroll position, click to scroll.
 * 2. SVG logo auto-trim: press logos exported with empty padding baked into the
 *    canvas look smaller than they are. For same-origin .svg logos we inline the
 *    file, measure the visible artwork with getBBox(), set a tight viewBox and
 *    publish the real aspect ratio as --mq-logo-r so the CSS equal-area sizing
 *    works on the glyph, not the canvas. Any failure keeps the original <img>.
 *
 * Loaded as a module from the section (runs once per page); every `.mq` is
 * initialised idempotently, and again on theme-editor section reloads.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {Map<string, Promise<string | null>>} */
const svgCache = new Map();
let uid = 0;

/* ── Dots ─────────────────────────────────────────────────────────────── */

/** @param {HTMLElement} section */
function initDots(section) {
  const track = /** @type {HTMLElement | null} */ (section.querySelector('[data-mq-track]'));
  const dots = /** @type {HTMLElement[]} */ (Array.from(section.querySelectorAll('[data-mq-dot]')));
  if (!track || dots.length === 0) return;

  const slides = () => /** @type {HTMLElement[]} */ (Array.from(track.querySelectorAll('[data-mq-slide]')));

  /** @param {number} idx */
  const setActive = (idx) => {
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === idx));
  };

  let ticking = false;
  track.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const slideWidth = slides()[0]?.offsetWidth || track.clientWidth;
        if (!slideWidth) return;
        const idx = Math.round(Math.abs(track.scrollLeft) / slideWidth);
        setActive(Math.max(0, Math.min(dots.length - 1, idx)));
      });
    },
    { passive: true }
  );

  dots.forEach((dot) => {
    dot.addEventListener('click', () => {
      const slide = slides()[Number(dot.dataset.mqDot)];
      if (!slide) return;
      // offsetLeft is relative to the offsetParent, not the track — use rects.
      const left = slide.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
      track.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    });
  });
}

/* ── SVG logo trim ────────────────────────────────────────────────────── */

/** @param {string} url */
function isSvgUrl(url) {
  try {
    return new URL(url, window.location.href).pathname.toLowerCase().endsWith('.svg');
  } catch {
    return false;
  }
}

/** @param {string} url */
function fetchSvg(url) {
  let request = svgCache.get(url);
  if (!request) {
    request = fetch(url)
      .then((res) => {
        const type = res.headers.get('content-type') || '';
        return res.ok && type.includes('svg') ? res.text() : null;
      })
      .catch(() => null); // CORS / network failure → keep the <img>
    svgCache.set(url, request);
  }
  return request;
}

/**
 * Parse + sanitise SVG markup. Returns null when it can't be inlined safely.
 * @param {string} text
 * @returns {SVGSVGElement | null}
 */
function parseSvg(text) {
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const svg = doc.documentElement;
  if (!svg || svg.nodeName.toLowerCase() !== 'svg' || doc.querySelector('parsererror')) return null;
  // Inline <style> would leak into the page; leave such files as <img>.
  if (svg.querySelector('style')) return null;

  svg.querySelectorAll('script, foreignObject').forEach((node) => node.remove());

  const all = [svg, ...Array.from(svg.querySelectorAll('*'))];
  for (const el of all) {
    for (const attr of Array.from(el.attributes)) {
      if (/^on/i.test(attr.name) || /^\s*javascript:/i.test(attr.value)) el.removeAttribute(attr.name);
    }
  }

  // Scope ids (clipPath/gradients) so multiple inlined logos can't collide.
  const prefix = `mq-logo-${++uid}-`;
  const ids = new Set();
  svg.querySelectorAll('[id]').forEach((el) => {
    ids.add(el.id);
    el.id = prefix + el.id;
  });
  if (ids.size) {
    for (const el of all) {
      for (const attr of Array.from(el.attributes)) {
        const value = attr.value;
        let next = value.replace(/url\(\s*(['"]?)#([^'")\s]+)\1\s*\)/g, (match, quote, id) =>
          ids.has(id) ? `url(#${prefix}${id})` : match
        );
        if (/(^|:)href$/.test(attr.name) && next.startsWith('#') && ids.has(next.slice(1))) {
          next = `#${prefix}${next.slice(1)}`;
        }
        if (next !== value) el.setAttribute(attr.name, next);
      }
    }
  }

  return /** @type {SVGSVGElement} */ (document.importNode(svg, true));
}

/**
 * @param {SVGSVGElement} svg
 * @returns {{x: number, y: number, width: number, height: number} | null}
 */
function readViewBox(svg) {
  const parts = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
  if (parts.length === 4 && parts.every(Number.isFinite) && parts[2] > 0 && parts[3] > 0) {
    return { x: parts[0], y: parts[1], width: parts[2], height: parts[3] };
  }
  const width = parseFloat(svg.getAttribute('width') || '');
  const height = parseFloat(svg.getAttribute('height') || '');
  if (width > 0 && height > 0) return { x: 0, y: 0, width, height };
  return null;
}

/** @param {HTMLImageElement} img */
async function trimLogo(img) {
  if (img.dataset.mqTrim) return;
  img.dataset.mqTrim = 'pending';

  const url = img.src;
  if (!url || !isSvgUrl(url)) return;

  const text = await fetchSvg(url);
  if (!text || !img.isConnected) return;

  const svg = parseSvg(text);
  if (!svg) return;

  const canvas = readViewBox(svg);
  if (!canvas) return;

  // Size comes from CSS (.mq__logo); drop the file's fixed dimensions.
  svg.setAttribute('viewBox', `${canvas.x} ${canvas.y} ${canvas.width} ${canvas.height}`);
  svg.removeAttribute('width');
  svg.removeAttribute('height');
  svg.setAttribute('class', img.className);
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', img.alt);
  svg.setAttribute('focusable', 'false');

  const wrap = /** @type {HTMLElement | null} */ (img.closest('.mq__logo-wrap'));
  img.replaceWith(svg);

  /** @type {DOMRect | null} */
  let box = null;
  try {
    box = svg.getBBox();
  } catch {
    box = null; // not rendered (e.g. display:none ancestor) — keep canvas viewBox
  }
  if (!box || box.width <= 0 || box.height <= 0) return;

  // Clamp to the original canvas: anything outside it was never visible.
  const x0 = Math.max(box.x, canvas.x);
  const y0 = Math.max(box.y, canvas.y);
  const x1 = Math.min(box.x + box.width, canvas.x + canvas.width);
  const y1 = Math.min(box.y + box.height, canvas.y + canvas.height);
  const width = x1 - x0;
  const height = y1 - y0;
  if (width <= 0 || height <= 0) return;

  const round = (/** @type {number} */ n) => Math.round(n * 1000) / 1000;
  svg.setAttribute('viewBox', `${round(x0)} ${round(y0)} ${round(width)} ${round(height)}`);
  wrap?.style.setProperty('--mq-logo-r', (width / height).toFixed(4));
  svg.setAttribute('data-mq-trim', 'done');
}

/* ── Init ─────────────────────────────────────────────────────────────── */

/** @param {ParentNode} root */
function init(root) {
  root.querySelectorAll('.mq').forEach((node) => {
    const section = /** @type {HTMLElement} */ (node);
    if (section.dataset.mqInit === 'true') return;
    section.dataset.mqInit = 'true';

    initDots(section);
    section.querySelectorAll('img.mq__logo').forEach((img) => trimLogo(/** @type {HTMLImageElement} */ (img)));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
} else {
  init(document);
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) init(event.target);
});
